// src/components/AgentLogTab.jsx
import { t } from '../lib/i18n.js'
import { useState, useEffect, useCallback } from 'react'
import { useDeliberateBackdropDismiss } from '../hooks/useDeliberateBackdropDismiss.js'
import {
  getLogs, getAllLogs, logEvent,
  LOG_CATEGORIES, formatLogDate,
} from '../lib/agentLog.js'
import { pill } from './desk/primitives.jsx'

const PAGE = 200

// ─── Add manual entry form ────────────────────────────────────────────────────
function AddEntryForm({ agent, onAdd, onClose }) {
  const backdropDismiss = useDeliberateBackdropDismiss(onClose)
  const [category, setCategory] = useState('info')
  const [message,  setMessage]  = useState('')
  const [saving,   setSaving]   = useState(false)

  async function handleSave() {
    if (!message.trim()) return
    setSaving(true)
    await logEvent(agent.id, agent.name, category, message.trim())
    onAdd()
    setSaving(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      {...backdropDismiss}>
      <div className="bg-card border border-border rounded-xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="font-semibold text-sm">Add log entry</div>
            <div className="text-xs text-muted-foreground">{agent.name}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-2">{t('log_category_label')}</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(LOG_CATEGORIES).map(([key, meta]) => (
                <button key={key} onClick={() => setCategory(key)}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-medium transition-all ${
                    category === key ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:border-primary/40'
                  }`}>
                  <span>{meta.icon}</span><span>{meta.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">{t('log_message_label')}</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              rows={3} className="w-full rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              {...{placeholder: t('log_message_placeholder')}} />
          </div>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-accent">{t('log_cancel_btn')}</button>
          <button onClick={handleSave} disabled={saving || !message.trim()}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving ? '…' : t('log_save_btn')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Single log entry row ─────────────────────────────────────────────────────
export function LogRow({ entry, showAgent }) {
  const meta = LOG_CATEGORIES[entry.category] || LOG_CATEGORIES.info
  const by = entry.by && entry.by !== 'Admin' ? entry.by : null

  return (
    <div className="flex gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="shrink-0 w-7 h-7 rounded-full bg-muted/60 flex items-center justify-center text-sm mt-0.5">
        {meta.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {showAgent && <span className="text-xs font-semibold">{entry.agent_name || entry.agentName}</span>}
          <span className={`text-xs font-medium ${meta.color}`}>{meta.label}</span>
          <span className="text-xs text-muted-foreground">{formatLogDate(entry.ts)}</span>
          {by && <span className="text-xs text-muted-foreground/60">· {by}</span>}
        </div>
        <p className="text-sm mt-0.5 text-foreground/90 leading-snug">{entry.message}</p>
      </div>
    </div>
  )
}

// ─── Per-agent log panel ──────────────────────────────────────────────────────
function AgentLogPanel({ agent, onAdd }) {
  const [logs,      setLogs]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [expanded,  setExpanded]  = useState(false)
  const [adding,    setAdding]    = useState(false)
  const [catFilter, setCatFilter] = useState('all')

  const refresh = useCallback(async () => {
    setLoading(true)
    const data = await getLogs(agent.id)
    setLogs(data)
    setLoading(false)
  }, [agent.id])

  useEffect(() => { refresh() }, [refresh])
  useEffect(() => { refresh() }, [onAdd, refresh])

  const filtered = catFilter === 'all' ? logs : logs.filter(e => e.category === catFilter)
  const SHOW = expanded ? filtered.length : 3

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div>
          <span className="font-semibold text-sm">{agent.name}</span>
          <span className="ml-2 text-xs text-muted-foreground">{agent.role}</span>
          {!loading && <span className="ml-2 text-xs text-muted-foreground/60">{logs.length} events</span>}
        </div>
        <button onClick={() => setAdding(true)} className="text-xs font-medium text-primary hover:underline">
          {t('log_add_entry')}
        </button>
      </div>

      {!loading && logs.length > 0 && (
        <div className="px-4 py-2 border-b border-border/50 flex gap-1.5 overflow-x-auto">
          <button onClick={() => setCatFilter('all')}
            className={pill('none', { door: true, extra: `font-medium ${catFilter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-transparent hover:text-foreground'}` })}>
            {t('log_all_count')} ({logs.length})
          </button>
          {Object.entries(LOG_CATEGORIES).map(([key, meta]) => {
            const count = logs.filter(e => e.category === key).length
            if (!count) return null
            return (
              <button key={key} onClick={() => setCatFilter(key)}
                className={pill('none', { door: true, extra: `font-medium ${catFilter === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-transparent hover:text-foreground'}` })}>
                {meta.icon} {meta.label} ({count})
              </button>
            )
          })}
        </div>
      )}

      <div className="px-4">
        {loading ? (
          <div className="py-6 text-center text-muted-foreground text-xs">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground text-xs">{t('log_no_events_long')}</div>
        ) : (
          <>
            {filtered.slice(0, SHOW).map(entry => (
              <LogRow key={entry.id} entry={entry} showAgent={false} />
            ))}
            {filtered.length > 3 && (
              <button onClick={() => setExpanded(e => !e)}
                className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
                {expanded ? '▲ Show less' : `▼ Show ${filtered.length - 3} more`}
              </button>
            )}
          </>
        )}
      </div>

      {adding && (
        <AddEntryForm agent={agent} onAdd={() => { refresh(); onAdd?.() }} onClose={() => setAdding(false)} />
      )}
    </div>
  )
}

// ─── Main tab ─────────────────────────────────────────────────────────────────
export function AgentLogTab({ agents }) {
  const [agentFilter, setAgentFilter] = useState('all')
  const [catFilter,   setCatFilter]   = useState('all')
  const [view,        setView]        = useState('timeline')
  const [tick,        setTick]        = useState(0)

  // Timeline state
  const [allLogs,  setAllLogs]  = useState([])
  const [total,    setTotal]    = useState(0)
  const [offset,   setOffset]   = useState(0)
  const [loading,  setLoading]  = useState(true)

  const sorted = [...agents].sort((a, b) => a.name.localeCompare(b.name))

  const loadTimeline = useCallback(async (off = 0, cat = catFilter, agentF = agentFilter) => {
    setLoading(true)
    const { entries, total: t } = await getAllLogs({ category: cat === 'all' ? '' : cat, offset: off })
    // Filter by agent client-side if needed (Supabase version loads all, we filter here)
    const filtered = agentF === 'all' ? entries : entries.filter(e => (e.agent_id || e.agentId) === agentF)
    setAllLogs(off === 0 ? filtered : prev => [...prev, ...filtered])
    setTotal(t)
    setOffset(off)
    setLoading(false)
  }, [catFilter, agentFilter])

  useEffect(() => {
    if (view === 'timeline') loadTimeline(0, catFilter === 'all' ? '' : catFilter, agentFilter)
  }, [view, tick, catFilter, agentFilter])

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        <select value={agentFilter} onChange={e => { setAgentFilter(e.target.value); setOffset(0) }}
          className="w-full sm:w-auto rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring">
          <option value="all">{t('all_agents')}</option>
          {sorted.map(a => <option key={a.id} value={a.id}>{a.name}{a.role ? ` — ${a.role}` : ''}</option>)}
        </select>

        <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setOffset(0) }}
          className="w-full sm:w-auto rounded-lg border border-input px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring">
          <option value="all">{t('all_categories')}</option>
          {Object.entries(LOG_CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>

        <div className="hidden sm:block sm:ml-auto text-xs text-muted-foreground">{t('log_events_auto')}</div>
      </div>

      {/* Timeline */}
      {view === 'timeline' && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-sm font-semibold">
            {t('log_timeline_title')} — {total} {t('log_entries')}
          </div>
          <div className="px-4">
            {loading && allLogs.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
            ) : allLogs.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">{t('log_no_events_short')}</div>
            ) : (
              <>
                {allLogs.map(entry => (
                  <LogRow key={entry.id} entry={entry} showAgent={true} />
                ))}
                {allLogs.length < total && (
                  <button onClick={() => loadTimeline(offset + PAGE)} disabled={loading}
                    className="w-full py-3 text-xs text-muted-foreground hover:text-foreground transition-colors border-t border-border/50">
                    {loading ? 'Loading…' : `▼ Load more (${total - allLogs.length} remaining)`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

    </div>
  )
}
